<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="KCPS VNコネクト 無料枠選択"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col>
                <span
                  >品目が1Gbps以下のVNコネクトを選択し、無料枠に変更することができます。</span
                ><br />
                <span :class="{ 'text-red': disabledEditBtn }"
                  >品目が2Gbps以上のVNコネクトは無料枠に変更することができません。</span
                >
              </v-col>
            </v-row>
            <app-table
              primary-key="cloudVnConnectAct.wnumber"
              :fields="vnConnects.fields"
              :items="vnConnectList!.cloudVnConnectsList"
              :selectable="true"
              :hover="true"
              :per-page="-1"
            >
              <!-- 選択チェックボックス（品目が2Gbps以上のVNコネクトは選択することができません） -->
              <template #cell(_selected)="data">
                <v-checkbox-btn
                  v-if="checkboxSelectable(data.item.cloudVnConnectCommon)"
                  :value="data.item"
                  v-model="vnConnects.selected"
                  @click.stop
                />
              </template>
              <template #cell(wnumber)="{ item }">
                {{ item.cloudVnConnectAct.wnumber }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.wnumber
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(vnConnectName)="{ item }">
                {{ item.cloudVnConnectAct.vnConnectName }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.vnConnectName
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(serviceKey)="{ item }">
                {{ item.cloudVnConnectCommon.serviceKey }}
              </template>
              <template #cell(vpnVnCode)="{ item }">
                <template
                  v-if="item.cloudVnConnectCommon.vpnVnCode.charAt(0) !== 'B'"
                >
                  {{
                    `${item.cloudVnConnectCommon.vpnVnCode}(${item.cloudVnConnectCommon.vnType})`
                  }}
                </template>
                <template v-else>
                  {{ item.cloudVnConnectCommon.vpnVnCode }}
                </template>
              </template>
              <template #cell(bandwidth)="{ item }">
                {{ item.cloudVnConnectCommon.bandwidthType }}({{
                  $filter.bandwidthTo(item.cloudVnConnectCommon.bandwidth)
                }})
              </template>
            </app-table>
          </v-container>
        </div>
        <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn
              @click="billingFree"
              variant="flat"
              color="primary"
              :disabled="disabledEditBtn"
              >変更</v-btn
            >
            <v-btn @click="cancel" variant="outlined" color="primary"
              >キャンセル</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader } from "@/components/AppTable.vue";
import type {
  CloudVnKcpsList,
  CloudVnKcpsCommonV2,
  CloudVnKcpsPut,
  CloudVnKcpsPutV2,
} from "@/apis/CloudVnKcpsApi";

export default defineComponent({
  name: "MultiCloudKcps3BillingFreeSelect",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    vnConnectList: {
      type: Object as PropType<CloudVnKcpsList>,
      required: true,
      default: undefined,
    },
    cloudVnKcpsKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      vnConnects: {
        sortBy: { key: "wnumber", order: "desc" },
        fields: [
          {
            key: "wnumber",
            title: "VNコネクト番号(Act/Sby)",
            width: "230px",
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名(Act/Sby)",
            width: "200px",
          },
          {
            key: "serviceKey",
            title: "KCPSユーザーID",
            width: "220px",
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            width: "250px",
          },
          {
            key: "bandwidth",
            title: "品目",
            width: "200px",
          },
        ] as AppTableHeader<CloudVnKcpsList>[],
        selected: [] as CloudVnKcpsList["cloudVnConnectsList"],
      },
      alertMsg: null as string | null,
      isLoaded: false,
      disabledEditBtn: false as boolean,
    };
  },
  async mounted() {
    /** 無料枠追加済み、チェックボックスを選択状態にする */
    this.vnConnects.selected = this.vnConnectList!.cloudVnConnectsList.filter(
      (item) => item.cloudVnConnectCommon.billingFree === true
    );

    /** すべてのVNコネクトの品目値が2Gbps以上の場合、2行目の文言を赤字かつ変更ボタンを非活性 */
    const selectableCount = this.vnConnectList!.cloudVnConnectsList.filter(
      (item) =>
        this.checkboxSelectable(
          item.cloudVnConnectCommon as CloudVnKcpsCommonV2
        )
    ).length;
    /** 選択可能な項目が0の場合、ボタンを無効化 */
    if (selectableCount === 0) {
      this.disabledEditBtn = true;
    }
    this.isLoaded = true;
  },
  methods: {
    /** true: 選択可能（品目が1Gbps以下）, false: 選択不可（品目が2Gbps以上） */
    checkboxSelectable(cloudVnKcpsCommon: CloudVnKcpsCommonV2): boolean {
      return (
        this.$filter.bandwidthToBps(cloudVnKcpsCommon.bandwidth)! <= 1000000000
      );
    },

    /** 無料枠選択 */
    async billingFree() {
      const selected = this.vnConnects.selected;
      if (selected.length > 1) {
        this.alertMsg = "無料枠VNコネクトは1つまで選択可能です。";
        return;
      }
      this.alertMsg = null;

      // 確認ダイアログの表示
      await this.$confirm("無料枠VNコネクトの変更を行います。よろしいですか？");

      // 設定済みの無料枠VNコネクト
      const initialChecked =
        this.vnConnectList!.cloudVnConnectsList.find(
          (vn) => vn.cloudVnConnectCommon.billingFree
        ) || null;
      // 現在選択されているVNコネクト
      const selectedWnumber = selected[0]?.cloudVnConnectAct?.wnumber || null;

      // 変更がない場合は処理終了
      if (
        initialChecked?.cloudVnConnectAct?.wnumber === selectedWnumber ||
        (initialChecked === null && selectedWnumber === null)
      ) {
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        return;
      }

      // 無料枠利用を解除する場合
      if (initialChecked && selected.length === 0) {
        await this.updateVnConnect(
          initialChecked.cloudVnConnectAct.wnumber,
          false,
          null
        );
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        return;
      }

      // 無料枠を別のVNコネクトに変更する場合
      if (initialChecked) {
        await this.updateVnConnect(
          initialChecked.cloudVnConnectAct.wnumber,
          false,
          selectedWnumber
        );
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        return;
      }

      // 新たに無料枠利用の回線を追加する場合
      if (!initialChecked && selectedWnumber) {
        await this.updateVnConnect(selectedWnumber, true, null);
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    // ヘルパーメソッド: VNコネクトを更新
    async updateVnConnect(
      wnumber: string,
      billingFree: boolean,
      assignFreeTo: string | null
    ) {
      // バージョンを判定
      const targetItem = this.vnConnectList!.cloudVnConnectsList.find(
        (item) => item.cloudVnConnectAct.wnumber === wnumber
      );
      const isV2 = targetItem?.cloudVnConnectCommon.version === "V2";

      if (isV2) {
        const detail = await this.$api.cloudVnKcps.getVnConnectV2(wnumber);
        const putData: CloudVnKcpsPutV2 = {
          ...detail,
          cloudVnConnectCommon: {
            ...detail.cloudVnConnectCommon,
            billingFree,
            assignFreeTo,
          },
        };
        await this.$api.cloudVnKcps.putVnConnectV2(
          this.cloudLineSeq,
          wnumber,
          putData
        );
      } else {
        const detail = await this.$api.cloudVnKcps.getVnConnect(wnumber);
        const putData: CloudVnKcpsPut = {
          ...detail,
          cloudVnConnectCommon: {
            ...detail.cloudVnConnectCommon,
            billingFree,
            assignFreeTo,
          },
        };
        await this.$api.cloudVnKcps.putVnConnect(
          this.cloudLineSeq,
          wnumber,
          putData
        );
      }
    },
  },
});
</script>
